import { createTheme } from '@mui/material/styles';
import { TOKEN_KEYS, toCssVar, LEGACY_ALIASES } from './tokens';
import { getSkin } from './skins';

export const THEME_MODE_KEY = 'theme-mode';
export const THEME_SKIN_KEY = 'theme-skin';

export function readStoredMode() {
  try {
    const stored = localStorage.getItem(THEME_MODE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function readStoredSkinId() {
  try {
    return localStorage.getItem(THEME_SKIN_KEY) || 'default';
  } catch {
    return 'default';
  }
}

export function resolveSkinTokens(skinId, mode) {
  const skin = getSkin(skinId);
  return skin[mode] || skin.light;
}

export function applyCssVars(tokens) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  TOKEN_KEYS.forEach((key) => {
    const value = tokens[key];
    if (value != null) {
      root.style.setProperty(toCssVar(key), value);
    }
  });
  Object.entries(LEGACY_ALIASES).forEach(([legacy, modern]) => {
    root.style.setProperty(legacy, `var(${modern})`);
  });
}

export function createMuiThemeFromTokens(tokens, mode) {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        dark: tokens.primaryHover,
        contrastText: tokens.primaryContrast,
      },
      secondary: {
        main: tokens.accent,
      },
      error: {
        main: tokens.error,
        contrastText: tokens.errorContrast,
      },
      warning: {
        main: tokens.warning,
      },
      success: {
        main: tokens.success,
      },
      info: {
        main: tokens.info,
      },
      background: {
        default: tokens.bgDefault,
        paper: tokens.bgPaper,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
        disabled: tokens.textDisabled,
      },
      divider: tokens.divider,
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            overflow: 'hidden',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '10px 16px',
            gap: 8,
            justifyContent: 'flex-end',
            alignItems: 'center',
            backgroundColor: 'var(--rd-bg-dialog-actions)',
            '& > :not(:first-of-type)': {
              marginLeft: 0,
            },
          },
        },
      },
      // MUI leaves input fills transparent, which only works while paper is
      // white. Skins with a tinted paper need an explicit field fill.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--rd-bg-field)',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--rd-bg-field)',
          },
        },
      },
    },
  });
}

export function applySkin(skinId, mode) {
  const tokens = resolveSkinTokens(skinId, mode);
  applyCssVars(tokens);
  return createMuiThemeFromTokens(tokens, mode);
}

if (typeof document !== 'undefined') {
  try {
    applyCssVars(resolveSkinTokens(readStoredSkinId(), readStoredMode()));
  } catch {
    // ignore storage / DOM errors during module init
  }
}
